import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { CALENDAR_VISIBILITY_TOOGLES_IDENTIFIER, DEFAULT_CALENDAR_PREFERENCES } from '@/app/(main)/calendar/consts/calendar-user-settings';
import { getUserSettingsQueryKey } from '@/consts/user-settings';
import { useGetCalendarPreferencesQuery } from '@/app/(main)/calendar/hooks/useGetCalendarPreferencesQuery';
import { useUpdateCalendarPreferencesMutation } from '@/app/(main)/calendar/hooks/useUpdateCalendarPreferencesMutation';
import type { CalendarPreferences, CalendarViewMode, CalendarVisibilityState } from '@/types/calendarTypes';

type PersistPreferencesOptions = {
  onError?: () => void;
};

export function useCalendarPreferences() {
  const queryClient = useQueryClient();
  const { data: preferences = DEFAULT_CALENDAR_PREFERENCES, isPending: isLoadingInitial } =
    useGetCalendarPreferencesQuery();
  const { mutate, isPending: isSaving } = useUpdateCalendarPreferencesMutation();

  const persistPreferences = useCallback(
    (patch: Partial<CalendarPreferences>, options?: PersistPreferencesOptions) => {
      const current =
        queryClient.getQueryData<CalendarPreferences>(
          getUserSettingsQueryKey(CALENDAR_VISIBILITY_TOOGLES_IDENTIFIER)
        ) ?? DEFAULT_CALENDAR_PREFERENCES;

      mutate(
        {
          view: patch.view ?? current.view,
          visibility: patch.visibility ?? current.visibility,
        },
        {
          onError: options?.onError,
        }
      );
    },
    [mutate, queryClient]
  );

  const setView = useCallback(
    (view: CalendarViewMode, options?: PersistPreferencesOptions) => persistPreferences({ view }, options),
    [persistPreferences]
  );

  const setVisibility = useCallback(
    (visibility: CalendarVisibilityState, options?: PersistPreferencesOptions) =>
      persistPreferences({ visibility }, options),
    [persistPreferences]
  );

  return {
    view: preferences.view,
    visibility: preferences.visibility,
    isLoadingInitial,
    isSaving,
    setView,
    setVisibility,
  };
}
